import React, { Fragment, lazy, Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router';
import { Layout } from './components/Layout';
import { Loading } from './components/loading/Loading';
import { TranslateWidget } from './components/TranslateWidget';
import ApiAuthorizationRoutes from './components/api-authorization/ApiAuthorizationRoutes';
import { ApplicationPaths } from './components/api-authorization/ApiAuthorizationConstants';
import 'animate.css';
import './appCommon.css'

// Always loaded — visible on every page
import { Home } from './components/home/Home';

// Lazy-loaded route components
const About             = lazy(() => import('./components/about/About').then(m => ({ default: m.About })));
const Links             = lazy(() => import('./components/links/Links').then(m => ({ default: m.Links })));
const MediaGallery      = lazy(() => import('./components/gallery/MediaGallery').then(m => ({ default: m.MediaGallery })));

const Shop              = lazy(() => import('./components/shop/Shop').then(m => ({ default: m.Shop })));
const ShopFaq           = lazy(() => import('./components/shop/faq/ShopFaq').then(m => ({ default: m.ShopFaq })));
const ShopItemDetails   = lazy(() => import('./components/shop/item/details/ShopItemDetails').then(m => ({ default: m.ShopItemDetails })));
const ShopCart          = lazy(() => import('./components/shop/cart/ShopCart').then(m => ({ default: m.ShopCart })));
const MyProfile         = lazy(() => import('./components/shop/myProfile/MyProfile').then(m => ({ default: m.MyProfile })));
const OrderDetails      = lazy(() => import('./components/shop/orderDetails/OrderDetails').then(m => ({ default: m.OrderDetails })));
const SuccessPayment    = lazy(() => import('./components/shop/payment/successPayment/SuccessPayment').then(m => ({ default: m.SuccessPayment })));

const ManagementDashboard   = lazy(() => import('./components/management/ManagementDashboard').then(m => ({ default: m.ManagementDashboard })));
const AboutManagement       = lazy(() => import('./components/about/management/AboutManagement').then(m => ({ default: m.AboutManagement })));
const ShopManagementDashboard = lazy(() => import('./components/shop/management/ShopManagementDashboard').then(m => ({ default: m.ShopManagementDashboard })));
const ShopItemCategoriesList  = lazy(() => import('./components/shop/management/shopItemCategories/ShopItemCategoriesList').then(m => ({ default: m.ShopItemCategoriesList })));
const ShopItemsList           = lazy(() => import('./components/shop/management/shopItems/ShopItemsList').then(m => ({ default: m.ShopItemsList })));
const ShopDiscountList        = lazy(() => import('./components/shop/management/discounts/ShopDiscountList').then(m => ({ default: m.ShopDiscountList })));
const ShopDeliveriesList      = lazy(() => import('./components/shop/management/deliveries/ShopDeliveriesList').then(m => ({ default: m.ShopDeliveriesList })));
const UsersList               = lazy(() => import('./components/shop/management/users/UsersList').then(m => ({ default: m.UsersList })));
const ShopOrdersList          = lazy(() => import('./components/shop/management/shopOrders/ShopOrdersList').then(m => ({ default: m.ShopOrdersList })));
const ShopOrderManage         = lazy(() => import('./components/shop/management/shopOrders/orderManage/ShopOrderManage').then(m => ({ default: m.ShopOrderManage })));
const MailConfig              = lazy(() => import('./components/newsletters/mailConfig/MailConfig').then(m => ({ default: m.MailConfig })));
const IllustrationsList       = lazy(() => import('./components/gallery/management/IllustrationsList').then(m => ({ default: m.IllustrationsList })));

const AccessDenied  = lazy(() => import('./components/errors/accessDenied/AccessDenied').then(m => ({ default: m.AccessDenied })));
const NotFound      = lazy(() => import('./components/errors/notFound/NotFound').then(m => ({ default: m.NotFound })));

export default function App() {

    return (
      <Fragment>
          <Loading />
          <TranslateWidget />
          <Layout>
              <Suspense fallback={null}>
                 <Switch>
                    { /* General info */ }
                     <Route exact path='/' component={Home} />
                     <Route exact path='/illustration' component={MediaGallery} />
                     <Route exact path='/about' component={About} />
                     <Route exact path='/links' component={Links} />

                     { /* Management */ }
                     <Route exact path='/management/main' component={ManagementDashboard} />
                     <Route exact path='/management/about' component={AboutManagement} />

                    { /* Shop */ }
                     <Route exact path='/shop' component={Shop} />
                     <Route exact path='/faq' component={ShopFaq} />
                     <Route exact path='/shop/:itemId' render={({ match }) =>
                        <ShopItemDetails itemId={match.params.itemId} />}
                     />
                     <Route exact path='/cart' component={ShopCart} />
                     <Route exact path='/profile' component={MyProfile} />
                     <Route exact path='/order/:orderId' render={({ match }) =>
                         <OrderDetails orderId={match.params.orderId} />}
                      />
                     <Route exact path='/successPayment' component={SuccessPayment} />

                    { /* Shop management */ }
                     <Route exact path='/shop/management/main' component={ShopManagementDashboard} />
                     <Route exact path='/shop/management/categories' component={ShopItemCategoriesList} />
                     <Route exact path='/shop/management/items' component={ShopItemsList} />
                     <Route exact path='/shop/management/discounts' component={ShopDiscountList} />
                     <Route exact path='/shop/management/deliveries' component={ShopDeliveriesList} />
                     <Route exact path='/shop/management/users' component={UsersList} />
                     <Route exact path='/shop/management/orders' component={ShopOrdersList} />
                      <Route exact path='/order/manage/:orderId' render={({ match }) =>
                          <ShopOrderManage orderId={match.params.orderId} />}
                      />
                      <Route exact path='/shop/management/email/setup' component={MailConfig} />

                    { /* Gallery management */ }
                     <Route exact path='/management/illustrations' component={IllustrationsList} />

                    { /* Errors */ }
                     <Route exact path='/403' component={AccessDenied} />
                     <Route exact path='/404' component={NotFound} />

                     { /* Authorization */ }
                     <Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} />

                     { /* Fallback */ }
                     <Route path="*" render={() => <Redirect to="/404" />} />
                 </Switch>
              </Suspense>
          </Layout>
      </Fragment>
    );
}
